import { Link } from 'react-router-dom';

const industryStats = [
  {
    stat: '44%',
    label: 'of American adults have no life insurance',
    source: 'LIMRA 2024 Insurance Barometer Study',
    icon: 'ri-user-unfollow-line',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
  },
  {
    stat: '3x',
    label: 'Most people overestimate the cost of term life insurance',
    source: 'Life Insurance & Market Research Assoc.',
    icon: 'ri-money-dollar-circle-line',
    color: 'text-[#D97642]',
    bgColor: 'bg-orange-50',
  },
  {
    stat: '$28/mo',
    label: 'Average term life rate for healthy 35-year-old in Indiana',
    source: '500K, 20-year term — 2025 carrier data',
    icon: 'ri-shield-check-line',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    stat: '10-12x',
    label: 'Annual income coverage most financial experts recommend',
    source: 'CFP Board & NAIC guidelines',
    icon: 'ri-bar-chart-line',
    color: 'text-[#1A3A52]',
    bgColor: 'bg-slate-50',
  },
];

const whyMatters = [
  {
    icon: 'ri-home-heart-line',
    title: 'Mortgage & Debt Protection',
    body: 'The average Indiana homeowner carries $187,000 in mortgage debt. Without life insurance, a surviving spouse can be forced to sell the family home within 6 months. A 20-year term policy eliminates that risk for as little as $30/month.',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'College & Education Planning',
    body: '4-year in-state tuition at Indiana University is projected to reach $132,000 by 2040. A properly structured whole life or IUL policy can fund your children\'s education tax-free while also providing lifetime death benefit protection.',
  },
  {
    icon: 'ri-parent-line',
    title: 'Income Replacement for Indiana Families',
    body: 'A 35-year-old earning $65,000/year has roughly $1.95 million in future earning potential. Life insurance replaces that economic value instantly — protecting your family\'s standard of living regardless of what happens.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Final Expense & Funeral Costs',
    body: 'The average Indiana funeral now costs $9,000–$12,500 (NFDA, 2024). Final expense insurance from as little as $40/month ensures your family never has to carry that financial burden during the most difficult time of their lives.',
  },
];

export default function CredibilityStats() {
  return (
    <section
      id="indiana-insurance-stats"
      className="py-16 md:py-24 bg-[#F7F9FB]"
      aria-label="Indiana life insurance statistics and coverage insights"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#1A3A52]/8 text-[#1A3A52] text-[12px] font-semibold uppercase tracking-widest rounded-full border border-[#1A3A52]/15 mb-5">
            Life Insurance in Indiana — By the Numbers
          </span>
          <h2 className="text-[26px] sm:text-[32px] md:text-[38px] font-bold text-[#1A3A52] leading-tight">
            Why Every Indiana Family Needs Life Insurance
          </h2>
          <p className="mt-4 text-[15px] md:text-[16px] text-[#4A5568] max-w-[680px] mx-auto leading-relaxed">
            Data from LIMRA, the NAIC, and leading carriers shows that most Indiana families are
            significantly underinsured — often by millions of dollars. Here's what the research says.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14 md:mb-16">
          {industryStats.map((item, i) => (
            <article
              key={i}
              className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-3"
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.bgColor} flex-shrink-0`}>
                <i className={`${item.icon} text-[22px] ${item.color}`}></i>
              </div>
              <div className={`text-[38px] font-extrabold leading-none tracking-tight ${item.color}`}>
                {item.stat}
              </div>
              <p className="text-[14px] font-semibold text-[#1A3A52] leading-snug">
                {item.label}
              </p>
              <p className="text-[12px] text-[#718096] mt-auto pt-2 border-t border-gray-100">
                <i className="ri-information-line mr-1"></i>
                {item.source}
              </p>
            </article>
          ))}
        </div>

        {/* Why It Matters Grid */}
        <div className="mb-12 md:mb-16">
          <h3 className="text-[20px] md:text-[24px] font-bold text-[#1A3A52] mb-8 text-center">
            What\'s Actually at Risk for Indiana Families
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyMatters.map((item, i) => (
              <article
                key={i}
                className="bg-white rounded-2xl border border-gray-100 p-7 flex gap-5"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-xl flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-[22px] text-[#D97642]`}></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-bold text-[#1A3A52] mb-2">{item.title}</h4>
                  <p className="text-[14px] text-[#4A5568] leading-relaxed">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Indiana-specific context */}
        <div className="bg-[#1A3A52] rounded-2xl p-7 md:p-10 text-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-[20px] md:text-[24px] font-bold mb-4 leading-snug">
                Indiana-Specific Coverage Considerations
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-[14px] text-white/80 leading-relaxed">
                  <i className="ri-check-double-line text-[#D97642] text-[17px] mt-0.5 flex-shrink-0"></i>
                  <span>
                    <strong className="text-white">Indiana median household income</strong> is $63,800 (U.S. Census, 2024) — meaning most families need at least $640K–$766K in coverage under the 10–12x rule.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-[14px] text-white/80 leading-relaxed">
                  <i className="ri-check-double-line text-[#D97642] text-[17px] mt-0.5 flex-shrink-0"></i>
                  <span>
                    <strong className="text-white">Indiana is an at-fault state</strong> with no state income tax on life insurance death benefits — making life insurance proceeds even more valuable for surviving families.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-[14px] text-white/80 leading-relaxed">
                  <i className="ri-check-double-line text-[#D97642] text-[17px] mt-0.5 flex-shrink-0"></i>
                  <span>
                    <strong className="text-white">Indexed Universal Life (IUL)</strong> is especially effective in Indiana because there is no state-level tax on cash value growth — giving you a tax-advantaged wealth-building tool unavailable in many other states.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-[14px] text-white/80 leading-relaxed">
                  <i className="ri-check-double-line text-[#D97642] text-[17px] mt-0.5 flex-shrink-0"></i>
                  <span>
                    <strong className="text-white">Phone &amp; virtual consultations</strong> mean you can compare 20+ A-rated carriers from any of Indiana's 92 counties without ever leaving home.
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-4">
              <div className="bg-white/10 rounded-xl p-5 border border-white/10">
                <p className="text-[13px] text-white/60 uppercase tracking-wide mb-2">Independent Agent Advantage</p>
                <p className="text-[15px] text-white leading-relaxed">
                  Unlike captive agents (State Farm, Allstate) who can only offer one company's products,
                  Hoosier Life Insurance shops <strong>20+ carriers simultaneously</strong> — including
                  Mutual of Omaha, Prudential, Nationwide, North American, and AIG — to find you the best
                  rate for your specific age, health, and coverage goals.
                </p>
              </div>
              <Link
                to="/compare"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D97642] text-white text-[14px] font-semibold rounded-xl hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-scales-3-line text-[16px]"></i>
                Compare Life Insurance Products
              </Link>
              <Link
                to="/instant-quote"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 border border-white/20 text-white text-[14px] font-semibold rounded-xl hover:bg-white/15 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line text-[16px]"></i>
                Get Your Free Quote in 60 Seconds
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
